import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { trackPurchase } from "../analytics/events";
import config from "../config";
import noimage from "../image/noimage.jpg";

export default function OrderConfirmation() {
  const location = useLocation();
  const [order,   setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  const orderNumber = new URLSearchParams(location.search).get("order");

  useEffect(() => {
    if (!orderNumber) { setError("Invalid order."); setLoading(false); return; }

    axios.get(`${config.apiUrl}/order/${orderNumber}`)
      .then((res) => {
        if (res.data.success) {
          setOrder(res.data.data);
          // ── FIRE CONVERSION EVENT ──────────────────────────────────────
          trackPurchase(res.data.data);
        } else {
          setError("Order not found.");
        }
      })
      .catch(() => setError("Could not load order details."))
      .finally(() => setLoading(false));
  }, [orderNumber]);

  if (loading) {
    return (
      <section className="mg-confirmation-page">
        <div className="container text-center py-5">
          <div className="spinner-border text-danger" role="status"></div>
          <p className="mt-3">Loading your order...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="mg-confirmation-page">
        <div className="container text-center py-5">
          <i className="fas fa-exclamation-circle text-danger" style={{ fontSize: "3rem" }}></i>
          <h2 className="mt-3">{error}</h2>
          <Link to="/shop" className="mg-conf-shop-btn mt-3">Back to Shop</Link>
        </div>
      </section>
    );
  }

  const paymentLabel = order.payment_method === "cod"
    ? "Cash on Delivery"
    : "Online Payment";

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Order Confirmed | MeraGym Shop</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      </HelmetProvider>

      <section className="mg-confirmation-page">
        <div className="container">
          <div className="mg-confirmation-card">
            {/* Success icon */}
            <div className="mg-confirmation-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <h1 className="mg-confirmation-heading">Order Placed Successfully!</h1>
            <p className="mg-confirmation-sub">
              Thank you, <strong>{order.name}</strong>! Your order has been confirmed.
              We'll send updates to <strong>{order.email}</strong>.
            </p>

            <div className="mg-confirmation-order-num">
              Order ID: <strong>{order.order_number}</strong>
            </div>

            <div className="row gy-4 mt-2">
              {/* ── Order Items ── */}
              <div className="col-lg-7">
                <div className="mg-conf-section">
                  <h3 className="mg-conf-section-title">Items Ordered</h3>
                  {order.items.map((item, i) => (
                    <div className="mg-conf-item" key={i}>
                      <div className="mg-conf-item-img">
                        <img
                          src={item.product_image || noimage}
                          alt={item.product_title}
                          onError={(e) => { e.target.src = noimage; }}
                        />
                      </div>
                      <div className="mg-conf-item-meta">
                        <p>{item.product_title}</p>
                        {item.variant_info && <span>{item.variant_info}</span>}
                        <small>Qty: {item.quantity}</small>
                      </div>
                      <div className="mg-conf-item-price">
                        ₹{Number(item.subtotal).toLocaleString("en-IN")}
                      </div>
                    </div>
                  ))}

                  <div className="mg-conf-totals">
                    <div className="mg-summary-row">
                      <span>Subtotal</span>
                      <span>₹{Number(order.subtotal).toLocaleString("en-IN")}</span>
                    </div>
                    <div className="mg-summary-row">
                      <span>Shipping</span>
                      <span className={order.shipping_amount == 0 ? "text-success fw-bold" : ""}>
                        {order.shipping_amount == 0 ? "FREE" : `₹${order.shipping_amount}`}
                      </span>
                    </div>
                    <div className="mg-summary-total">
                      <span>Total Paid</span>
                      <span>₹{Number(order.total_amount).toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Delivery & Payment ── */}
              <div className="col-lg-5">
                <div className="mg-conf-section">
                  <h3 className="mg-conf-section-title">Delivery Address</h3>
                  <div className="mg-conf-address">
                    <strong>{order.name}</strong>
                    <p>{order.address}</p>
                    <p>{order.city}{order.state ? `, ${order.state}` : ""} — {order.pincode}</p>
                    <p><i className="fas fa-phone me-1"></i>{order.phone}</p>
                    <p><i className="fas fa-envelope me-1"></i>{order.email}</p>
                  </div>
                </div>

                <div className="mg-conf-section mt-3">
                  <h3 className="mg-conf-section-title">Payment & Delivery</h3>
                  <div className="mg-conf-info-rows">
                    <div className="mg-conf-info-row">
                      <i className="fas fa-money-bill-wave"></i>
                      <div>
                        <strong>Payment</strong>
                        <span>{paymentLabel}</span>
                      </div>
                    </div>
                    <div className="mg-conf-info-row">
                      <i className="fas fa-truck"></i>
                      <div>
                        <strong>Estimated Delivery</strong>
                        <span>3–7 business days</span>
                      </div>
                    </div>
                    <div className="mg-conf-info-row">
                      <i className="fas fa-clock"></i>
                      <div>
                        <strong>Order Date</strong>
                        <span>{order.created_at}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="mg-conf-actions">
              <Link to="/shop" className="mg-conf-shop-btn">
                <i className="fas fa-shopping-bag me-2"></i>Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
